import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import * as Styled from "./styles";
import { Typography, Divider, Modal, Chip } from "@mui/material";
import Button from "components/atoms/Button";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import ProductCard from "../ProductCard";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  CatalogProductsProps,
  ProductPrices,
} from "store/modules/Catalog/types.store";
import { handleUpdateCatalogAreaRequest } from "store/modules/Catalog/thunk.store";
import { toast } from "utils/toast";
import { isGasDoPovoCatalogProduct } from "utils/gasDoPovo";

type CoverageAreaWithProducts = CoverageAreaProps & {
  productPrices?: ProductPrices[];
};

const MassEditPriceMenu = ({
  massEditData,
  handleCloseMassEditPrice,
  gasDoPovo = false,
}: {
  massEditData: CoverageAreaWithProducts[];
  handleCloseMassEditPrice: () => void;
  gasDoPovo?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { catalogProducts } = useAppSelector((state) => state.catalog);
  const [massEditProducts, setMassEditProducts] = useState<
    CatalogProductsProps[]
  >([]);
  const [productPrices, setProductPrices] = useState<ProductPrices[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCloseConfirmationModalOpen, setIsCloseConfirmationModalOpen] =
    useState(false);

  const filterMassEditProductsByCoverageArea = useMemo(() => {
    const excludeGasDoPovo = !gasDoPovo;

    const filtered =
      catalogProducts?.data?.filter((product: CatalogProductsProps) => {
        return massEditData.some((coverageArea: any) =>
          coverageArea.productPrices?.some(
            (productPrice: any) => productPrice.product.id === product.id,
          ),
        );
      }) ?? [];

    return excludeGasDoPovo
      ? filtered.filter((product) => !isGasDoPovoCatalogProduct(product))
      : filtered;
  }, [catalogProducts?.data, massEditData, gasDoPovo]);

  const handleProductPrices = useCallback(
    (productId: string, productPrice: number) => {
      const product = massEditProducts.find(
        (item: CatalogProductsProps) => item.id === productId,
      );

      if (!product) {
        return;
      }

      setProductPrices((prev: ProductPrices[]) => {
        const existingIndex = prev.findIndex(
          (item) => item.product.id === productId,
        );

        const updatedProductPrice: ProductPrices = {
          ...(existingIndex !== -1 ? prev[existingIndex] : {}),
          product,
          price: productPrice,
          description:
            existingIndex !== -1 ? prev[existingIndex].description : "",
          enabled: existingIndex !== -1 ? prev[existingIndex].enabled : true,
        };

        if (existingIndex !== -1) {
          return prev.map((item, index) =>
            index === existingIndex ? updatedProductPrice : item,
          );
        }

        return [...prev, updatedProductPrice];
      });
    },
    [massEditProducts, setProductPrices],
  );

  const handleAdjustPrices = useCallback(() => {
    return massEditData.map((coverageArea) => {
      const updatedProductPrices = coverageArea.productPrices?.map(
        (coverageAreaProductPrice) => {
          const productPriceFromState = productPrices.find(
            (productPriceState) =>
              productPriceState.product.id ===
              coverageAreaProductPrice.product.id,
          );

          if (!productPriceFromState) {
            return coverageAreaProductPrice;
          }

          return {
            ...coverageAreaProductPrice,
            price: productPriceFromState.price,
          };
        },
      );

      return {
        ...coverageArea,
        productPrices: updatedProductPrices || coverageArea.productPrices,
      };
    });
  }, [productPrices, massEditData]);

  const buildDealerPriceCatalogRequests = useCallback(
    (adjustedMassEditData: CoverageAreaWithProducts[]) => {
      return adjustedMassEditData.reduce((requests, coverageArea) => {
        // Envia todos os produtos da área de cobertura
        // Produtos alterados usam o novo preço, os demais mantêm o preço original
        const productPricesForPayload =
          coverageArea.productPrices?.map((coverageAreaProductPrice) => {
            const updatedProductPrice = productPrices.find(
              (pp) => pp.product.id === coverageAreaProductPrice.product.id,
            );

            return {
              productId: coverageAreaProductPrice.product.id,
              price:
                updatedProductPrice?.price ?? coverageAreaProductPrice.price,
              description: coverageAreaProductPrice.description,
              enabled: coverageAreaProductPrice.enabled,
              name: coverageAreaProductPrice.product.name,
            };
          }) || [];

        if (!productPricesForPayload.length) {
          return requests;
        }

        const request = dispatch(
          handleUpdateCatalogAreaRequest({
            data: productPricesForPayload,
            id: coverageArea.id,
            userId: coverageArea.dealer.id,
          }),
        );

        requests.push(request);

        return requests;
      }, [] as ReturnType<typeof dispatch>[]);
    },
    [productPrices, dispatch],
  );

  const onSubmit = useCallback(async () => {
    const adjustedMassEditData = handleAdjustPrices();

    const dealerPriceCatalogRequests =
      buildDealerPriceCatalogRequests(adjustedMassEditData);

    if (!dealerPriceCatalogRequests.length) {
      return;
    }

    const results = await Promise.all(dealerPriceCatalogRequests);

    const allSucceeded = results.every((result) =>
      handleUpdateCatalogAreaRequest.fulfilled.match(result as AnyAction),
    );

    if (!allSucceeded) {
      return;
    }

    toast.success("Preços atualizados com sucesso!");
    setIsConfirmationModalOpen(false);
    setProductPrices([]);
    handleCloseMassEditPrice();
  }, [
    buildDealerPriceCatalogRequests,
    handleAdjustPrices,
    handleCloseMassEditPrice,
  ]);

  // Verifica se há alterações nos preços
  const hasChanges = useMemo(() => {
    if (productPrices.length === 0) {
      return false;
    }

    return productPrices.some((productPrice) => {
      const originalProductPrice = massEditData
        .flatMap((coverageArea) => coverageArea.productPrices || [])
        .find((pp) => pp.product.id === productPrice.product.id);

      if (!originalProductPrice) {
        return true; // Novo preço adicionado
      }

      return originalProductPrice.price !== productPrice.price;
    });
  }, [productPrices, massEditData]);

  const handleClose = useCallback(() => {
    if (hasChanges) {
      setIsCloseConfirmationModalOpen(true);
    } else {
      handleCloseMassEditPrice();
    }
  }, [hasChanges, handleCloseMassEditPrice]);

  const handleConfirmClose = useCallback(() => {
    setProductPrices([]);
    setIsCloseConfirmationModalOpen(false);
    handleCloseMassEditPrice();
  }, [handleCloseMassEditPrice]);

  const handleCancelClose = useCallback(() => {
    setIsCloseConfirmationModalOpen(false);
  }, []);

  const renderCloseConfirmationModal = useMemo(() => {
    return (
      <Modal open={isCloseConfirmationModalOpen} onClose={handleCancelClose}>
        <Styled.ConfirmationModalContainer>
          <Typography variant="h6">Atenção</Typography>
          <Typography variant="body2">
            Você possui alterações não salvas. Deseja realmente fechar sem
            salvar?
          </Typography>
          <Divider sx={{ borderColor: "black" }} />
          <Typography variant="body2" fontWeight="bold">
            Revendas selecionadas:
          </Typography>
          <Styled.ChipsContainer>
            {massEditData.map((coverageArea) => (
              <Chip
                variant="outlined"
                key={coverageArea.id}
                label={coverageArea.name}
              />
            ))}
          </Styled.ChipsContainer>
          <Styled.ConfirmationModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancelClose}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmClose}
            >
              Fechar sem salvar
            </Button>
          </Styled.ConfirmationModalButtonsContainer>
        </Styled.ConfirmationModalContainer>
      </Modal>
    );
  }, [
    isCloseConfirmationModalOpen,
    massEditData,
    handleCancelClose,
    handleConfirmClose,
  ]);

  const renderConfirmationModal = useMemo(() => {
    return (
      <Modal
        open={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
      >
        <Styled.ConfirmationModalContainer>
          <Typography variant="h6">Atenção</Typography>
          <Typography variant="body2">
            O novo valor será aplicado em todas as áreas de cobertura
            selecionadas. Deseja salvar estas alterações?
          </Typography>
          <Divider sx={{ borderColor: "black" }} />
          <Typography variant="body2" fontWeight="bold">
            Revendas selecionadas:
          </Typography>
          <Styled.ChipsContainer>
            {massEditData.map((coverageArea) => (
              <Chip
                variant="outlined"
                key={coverageArea.id}
                label={coverageArea.name}
              />
            ))}
          </Styled.ChipsContainer>
          <Typography variant="body2" fontWeight="bold">
            Itens alterados:
          </Typography>
          <Styled.ConfirmationModalItemsContainer>
            {productPrices.map((product) => (
              <ProductCard
                key={product.product.id}
                product={product.product}
                onUpdate={handleProductPrices}
                hasDefinedPrice={product.price}
              />
            ))}
          </Styled.ConfirmationModalItemsContainer>
          <Styled.ConfirmationModalButtonsContainer>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsConfirmationModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSubmit()}
            >
              Confirmar
            </Button>
          </Styled.ConfirmationModalButtonsContainer>
        </Styled.ConfirmationModalContainer>
      </Modal>
    );
  }, [
    handleProductPrices,
    isConfirmationModalOpen,
    massEditData,
    onSubmit,
    productPrices,
  ]);

  useEffect(() => {
    if (catalogProducts?.data) {
      setMassEditProducts(filterMassEditProductsByCoverageArea || []);
    }
  }, [catalogProducts, filterMassEditProductsByCoverageArea]);

  return (
    <Styled.Container>
      <Typography variant="h5">Editar Preço</Typography>
      <Typography variant="body2">
        Defina o preço do(s) produto(s) que serão aplicados nas áreas de
        cobertura selecionadas.
      </Typography>
      <Divider sx={{ borderColor: "black" }} />

      <Styled.PriceChangeInfoContainer>
        <Typography variant="body2">
          A atualização de preços será aplicada apenas aos produtos listados no
          catálogo. Itens não cadastrados não serão incluídos nem terão seus
          preços alterados.
        </Typography>
      </Styled.PriceChangeInfoContainer>
      {massEditProducts?.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onUpdate={handleProductPrices}
          edit={massEditProducts.length > 0}
        />
      ))}

      <Styled.ButtonsContainer>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Fechar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsConfirmationModalOpen(true)}
        >
          Aplicar
        </Button>
      </Styled.ButtonsContainer>
      {renderCloseConfirmationModal}
      {renderConfirmationModal}
    </Styled.Container>
  );
};

export default MassEditPriceMenu;
